import { ISchema } from "../models/ISchema";
import { isEmptyOrWhitespace } from "@/utils";
import { IGetSchemasReponse } from "../models/IGetSchemasReponse";
import configService from "@/services/configService"
import { DeploymentPrefix, getCdnPrefix } from "../models/DeploymentPrefix";
import { Modal } from "ant-design-vue";
import { h } from "vue";
import { InjectableService } from "./InjectableService";
import { CdnSchemaField } from "../CdnSchema/models/CdnSchemaField";

export abstract class SchemaService<TSchema extends ISchema> extends InjectableService {

    // Cache schemas per service (keyed by injectableKey) AND per DeploymentPrefix
    // Map<injectableKey, Map<DeploymentPrefix, ISchema[]>>
    private static _schemaCacheMap: Map<string, Map<DeploymentPrefix, ISchema[]>> = new Map();

    // Allow static cache w/ generic constraint
    protected get schemaCache(): TSchema[] {
        if (isEmptyOrWhitespace(this.injectableKey)) return [];
        const deploymentPrefix = this._deploymentPrefix as DeploymentPrefix;
        const cacheForClass = SchemaService._schemaCacheMap.get(this.injectableKey);
        if (cacheForClass) {
            return (cacheForClass.get(deploymentPrefix) as TSchema[]) || [];
        }
        return [];
    }

    protected set schemaCache(schemas: TSchema[]) {
        if (isEmptyOrWhitespace(this.injectableKey)) {
            throw new Error(`${this.constructor.name} must implement a non-empty injectableKey`);
        }
        const deploymentPrefix = this._deploymentPrefix as DeploymentPrefix;
        let cacheForClass = SchemaService._schemaCacheMap.get(this.injectableKey);
        if (!cacheForClass) {
            cacheForClass = new Map<DeploymentPrefix, ISchema[]>();
            SchemaService._schemaCacheMap.set(this.injectableKey, cacheForClass);
        }
        cacheForClass.set(deploymentPrefix, schemas);
    }

    private _deploymentPrefix: string = configService.getApplicationDeploymentPrefix();
    private readonly _aplosCdnUrl: string = `https://cdn.aplos-nca.com/schemas/`;

    // Relative path to content for implementing feature area
    abstract cdnContentPath: string;

    public abstract getInternalSchemas(): Array<TSchema>;

    //TODO pull this from application configuration and populate on each impl of this base service
    private readonly _defaultVersion: string = "3.0.0";

    public async getSchemas(deploymentPrefix?: DeploymentPrefix, version: string = this._defaultVersion): Promise<Array<TSchema> | string> {

        if (isEmptyOrWhitespace(version)) throw Error("valid version is required");

        // Use the provided deploymentPrefix or the instance's default
        const effectivePrefix = deploymentPrefix ?? (this._deploymentPrefix as DeploymentPrefix);
        let contentUri: string | null = null;
        try {
            // Check cache first
            if (isEmptyOrWhitespace(this.injectableKey)) {
                throw new Error(`${this.constructor.name} must implement a non-empty injectableKey`);
            }
            const cacheKey = this.injectableKey;
            let cacheForClass = SchemaService._schemaCacheMap.get(cacheKey);
            if (cacheForClass && cacheForClass.has(effectivePrefix)) {
                return cacheForClass.get(effectivePrefix) as TSchema[];
            }

            contentUri = this.buildCdnContentUri(effectivePrefix, version);
            const response = await (async () => {
                const r = await fetch(contentUri as string, {
                    method: 'GET',
                    credentials: 'omit',   // closest to axios withCredentials: false
                    cache: 'no-cache',
                    headers: {
                        Accept: 'application/json, text/plain, */*'
                    }
                })

                const data = await r.text()

                // Normalize to the shape your existing code expects
                return {
                    ok: r.ok,
                    status: r.status,
                    data,
                    headers: {
                        'content-type': r.headers.get('content-type') ?? ''
                    },
                    request: {
                        responseURL: r.url
                    }
                }
            })()

            // Heuristic checks for redirect/captive-portal HTML responses:
            //
            // 1) Content-Type header not JSON (e.g. text/html)
            // 2) Final URL differs from the requested URL (XHR responseURL) => browser followed a redirect
            // 3) Body contains HTML tags as a fallback if headers are missing/incorrect
            const contentType = response.headers['content-type'] || '';
            const finalUrl = response.request.responseURL || null;
            const bodyText = String(response.data || '');
            const isRedirectReponseCode = response.status === 302;
            const isSuccessResponseCode = response.ok;

            const looksLikeHtml = /<html|<!doctype/i.test(bodyText);
            const isJsonType = /application\/json|\/json/i.test(contentType);

            if (isRedirectReponseCode || !isSuccessResponseCode || (!isJsonType && (looksLikeHtml || (finalUrl && finalUrl !== contentUri)))) {
                // Show a specific modal indicating a possible network redirect/captive portal
                Modal.error({
                    title: 'Schemas could not be loaded — possible network redirect',
                    content: h('div', [
                        h(
                            'p',
                            { style: 'margin-bottom: 12px; font-weight: normal;' },
                            'The request to load schema JSON returned HTML or was redirected. This often happens when a corporate firewall or captive portal redirects network traffic to an internal page.'
                        ),
                        h(
                            'p',
                            { style: 'margin-bottom: 12px; font-weight: normal;' },
                            [
                                'Please try opening the following URL in a new tab to inspect the response: ',
                                h('br'),
                                h('a', { href: contentUri, target: '_blank', rel: 'noreferrer' }, contentUri)
                            ]
                        ),
                        finalUrl && finalUrl !== contentUri
                            ? h('p', { style: 'margin-bottom: 0; font-weight: normal;' }, `Note: The browser was redirected to: ${finalUrl}`)
                            : null
                    ]),
                    okText: 'Ok',
                    width: 700
                });

                // Return the contentUri as your error sentinel (existing behavior)
                return contentUri as string;
            }

            // Otherwise try parse JSON (wrap to present better error message on parse failure)
            let jsonReponse: IGetSchemasReponse;
            try {
                jsonReponse = JSON.parse(response.data) as IGetSchemasReponse;
            } catch (parseError) {
                console.error('Failed to parse schema JSON from CDN:', parseError);
                Modal.error({
                    title: 'Invalid schema response',
                    content: h('div', [
                        h('p', { style: 'margin-bottom: 12px; font-weight: normal;' }, 'The schema endpoint returned data that could not be parsed as JSON.'),
                        h('p', { style: 'margin-bottom: 0; font-weight: normal;' }, [
                            'Open the URL in a new tab to inspect the response: ',
                            h('a', { href: contentUri, target: '_blank', rel: 'noreferrer' }, contentUri)
                        ])
                    ]),
                    okText: 'Ok',
                    width: 700
                });
                return contentUri as string;
            }

            const schemas = jsonReponse.schemas as Array<TSchema>;
            // Set cache for this injectable key and deployment prefix
            if (!cacheForClass) {
                cacheForClass = new Map<DeploymentPrefix, ISchema[]>();
                SchemaService._schemaCacheMap.set(cacheKey, cacheForClass);
            }
            cacheForClass.set(effectivePrefix, schemas);
            return schemas;

        } catch (error) {
            const isAxiosErr = !!(error && typeof error === 'object' && (error as any).isAxiosError);

            if (isAxiosErr) {
                console.error('Axios error message:', (error as any).message);
            } else {
                console.error('Network/fetch error loading schemas:', error);
            }

            Modal.error({
                title: 'Error loading schemas from Aplos CDN',
                content: h('div', [
                    h('p', { style: 'margin-bottom: 15px; margin-top: 10px; font-weight: normal;' }, 'Please ensure you can access the resources located at the following url:'),
                    h('p', { style: 'margin-bottom:' }, ['', h('a', { href: contentUri, target: '_blank' }, contentUri as string), '.'])
                ]),
                okText: 'Ok',
                width: 700
            });

            return contentUri as string;
        }
    }

    public buildCdnContentUri(deploymentPrefix?: DeploymentPrefix, version: string = "1.0.0"): string {
        const cdnPrefix = getCdnPrefix(deploymentPrefix ?? (this._deploymentPrefix as DeploymentPrefix));
        return `${this._aplosCdnUrl}${cdnPrefix}/${this.cdnContentPath}_${version}.json`;
    }

    /**
     * Recursively searches a field tree for the first field matching the given `fieldType`
     * at the specified nesting `level`.
     *
     * Level 1 (default) searches only top-level fields. Level 2 searches one level of nested
     * `fields` children, and so on. The search is depth-first and returns the first match found.
     *
     * @param fields - The array of fields to search. Typically sourced from a schema's `fields` property.
     * @param fieldType - The `field_type` value to match against. Accepts any `AdvancedAnalysisFieldType`,
     *   `JsonEditorSchemaType`, or raw string value.
     * @param level - The target nesting depth to search. Defaults to `1` (top-level fields only).
     * @param currentLevel - Internal recursion counter. Do not supply this argument directly.
     * @returns The first matching field of type `T` at the specified level, or `undefined` if not found.
     *
     * @example
     * // Find the top-level field typed as RuleSchema
     * const ruleField = SchemaService.tryGetFirstFieldOfType<AdvancedAnalysisField>(
     *     schema.fields ?? [],
     *     JsonEditorSchemaType.RuleSchema
     * );
     * if (ruleField) {
     *     advancedAnalysis.configuration[ruleField.name] = parsedRules;
     * }
     */
    public static tryGetFirstFieldOfType<T extends CdnSchemaField>(fields: Array<T>, fieldType: string, level: number = 1, currentLevel: number = 1): T | undefined {
        for (const field of fields) {
            if (field.field_type === fieldType && currentLevel === level) return field;
            if (field.fields?.length && currentLevel < level) {
                const found = this.tryGetFirstFieldOfType(field.fields as T[], fieldType, level, currentLevel + 1);
                if (found) return found;
            }
        }
        return undefined;
    }

}
